import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';

import { AuthProvider } from './providers/AuthProvider';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoadingSpinner } from './components/ui/loading-spinner';
import { AppShellWithSidebar } from './components/layout/AppShellWithSidebar';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { ComplianceProvider } from './contexts/ComplianceContext';
import { AMLProvider } from './contexts/AMLContext';
import { ComplianceErrorBoundary } from './components/ui/ComplianceErrorBoundary';

// Import cache utilities for global access
import './utils/cache-utils';

// Lazy load components for better performance
const ShadcnLoginPage = lazy(() => import('./pages/ShadcnLoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(module => ({ default: module.DashboardPage })));
const BillsAmortizationPage = lazy(() => import('./pages/BillsAmortizationPage').then(module => ({ default: module.BillsAmortizationPage })));
const PrepaymentsPageV2 = lazy(() => import('./components/prepayments-v2').then(module => ({ default: module.PrepaymentsPageV2 })));
const AccpayAllPageV2 = lazy(() => import('./pages/AccpayAllPageV2'));
const AccountPage = lazy(() => import('./pages/AccountPage').then(module => ({ default: module.AccountPage })));
const BillingPage = lazy(() => import('./pages/BillingPage').then(module => ({ default: module.BillingPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(module => ({ default: module.NotificationsPage })));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })));
const TagManagementPage = lazy(() => import('./pages/TagManagementPage').then(module => ({ default: module.TagManagementPage })));
const ComplianceDetailPage = lazy(() => import('./pages/ComplianceDetailPage').then(module => ({ default: module.ComplianceDetailPage })));
const InboxPage = lazy(() => import('./pages/InboxPage').then(module => ({ default: module.InboxPage })));
const XeroOrganizationSelector = lazy(() => import('./components/XeroOrganizationSelector').then(module => ({ default: module.XeroOrganizationSelector })));
const TailwindTest = lazy(() => import('./components/TailwindTest').then(module => ({ default: module.TailwindTest })));
const DraggableDialogDemo = lazy(() => import('./pages/DraggableDialogDemo'));
const EntityManagement = lazy(() => import('./pages/EntityManagement').then(module => ({ default: module.EntityManagement })));
const EntityExemptionsPage = lazy(() => import('./pages/EntityExemptionsPage').then(module => ({ default: module.EntityExemptionsPage })));
const TransactionExemptionsPage = lazy(() => import('./pages/TransactionExemptionsPage').then(module => ({ default: module.TransactionExemptionsPage })));
const ComplianceSettingsPage = lazy(() => import('./pages/ComplianceSettingsPage').then(module => ({ default: module.ComplianceSettingsPage })));
const AMLDetailPage = lazy(() => import('./pages/AMLDetailPage').then(module => ({ default: module.AMLDetailPage })));
const CustomScanPage = lazy(() => import('./pages/CustomScanPage').then(module => ({ default: module.CustomScanPage })));
const EmailForwardingPage = lazy(() => import('./pages/EmailForwardingPage').then(module => ({ default: module.EmailForwardingPage })));
const EmailForwardingAnalyticsPage = lazy(() => import('./pages/EmailForwardingAnalyticsPage').then(module => ({ default: module.EmailForwardingAnalyticsPage })));
const CollectedEmailsPage = lazy(() => import('./pages/CollectedEmailsPage').then(module => ({ default: module.CollectedEmailsPage })));
// Entity Setup Wizard - imports SimpleConnectionWizard as the actual implementation
const EntitySetupWizard = lazy(() => import('./components/entities/SimpleConnectionWizard').then(module => ({ default: module.SimpleConnectionWizard })));
// Xero Apps Admin Page
const XeroAppsPage = lazy(() => import('./pages/settings/XeroAppsPage').then(module => ({ default: module.XeroAppsPage })));
// Migration Admin Dashboard
const MigrationDashboardPage = lazy(() => import('./pages/admin/MigrationDashboardPage').then(module => ({ default: module.MigrationDashboardPage })));
// AI Training Pipeline Pages
const AIDocumentsPage = lazy(() => import('./pages/AIDocumentsPage'));
const AIDocumentReviewPage = lazy(() => import('./pages/AIDocumentReviewPage'));
const AIConfidenceDashboardPage = lazy(() => import('./pages/AIConfidenceDashboardPage'));

// Backward compatibility redirect components for old compliance URLs
function ComplianceRedirectWithEntity() {
  const { clientId, entityId } = useParams<{ clientId: string; entityId: string }>();
  if (!clientId || !entityId) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to={`/compliance/all/${clientId}/${entityId}`} replace />;
}

function ComplianceRedirectWithoutEntity() {
  const { clientId } = useParams<{ clientId: string }>();
  if (!clientId) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Navigate to={`/compliance/all/${clientId}`} replace />;
}

// Component to handle Xero configure redirect
const XeroConfigureRedirect = () => {
  const { clientId } = useParams<{ clientId: string }>();
  return <Navigate to={`/clients/${clientId}/entities`} replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<ShadcnLoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/tailwind-test" element={<TailwindTest />} />
            <Route path="/dialog-demo" element={<DraggableDialogDemo />} />

            {/* Protected routes with sidebar */}
            <Route element={
              <ProtectedRoute>
                <ComplianceErrorBoundary
                  showRetryButton={true}
                  onRetry={() => window.location.reload()}
                >
                  <ComplianceProvider>
                    <AMLProvider>
                      <AppShellWithSidebar />
                    </AMLProvider>
                  </ComplianceProvider>
                </ComplianceErrorBoundary>
              </ProtectedRoute>
            }>
              <Route path="/dashboard" element={<DashboardPage />} />
              
              {/* ACCPAY Routes - URL pattern matches Compliance: /accpay/{page}/{clientId}/{entityId} */}
              <Route path="/accpay" element={<Navigate to="/accpay/all" replace />} />
              <Route path="/accpay/prepayments/:clientId/:entityId" element={<PrepaymentsPageV2 />} />
              <Route path="/accpay/prepayments/:clientId" element={<PrepaymentsPageV2 />} />
              <Route path="/accpay/prepayments" element={<PrepaymentsPageV2 />} />
              <Route path="/accpay/all/:clientId/:entityId" element={<AccpayAllPageV2 />} />
              <Route path="/accpay/all/:clientId" element={<AccpayAllPageV2 />} />
              <Route path="/accpay/all" element={<AccpayAllPageV2 />} />
              
              {/* User Profile Pages */}
              <Route path="/account" element={<AccountPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              
              {/* Inbox */}
              <Route path="/inbox/comments" element={<InboxPage />} />
              <Route path="/inbox/emails/:clientId/:entityId" element={<CollectedEmailsPage />} />

              {/* Settings */}
              <Route path="/settings" element={<SettingsPage />} />
              
              {/* Tag Management */}
              <Route path="/settings/tags" element={<TagManagementPage />} />

              {/* Xero Apps Admin - platform_admin only */}
              <Route
                path="/settings/xero-apps"
                element={
                  <ProtectedRoute requiredRole="platform_admin">
                    <XeroAppsPage />
                  </ProtectedRoute>
                }
              />

              {/* Migration Admin Dashboard - platform_admin only */}
              <Route
                path="/admin/migration"
                element={
                  <ProtectedRoute requiredRole="platform_admin">
                    <MigrationDashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Compliance Detail Page - New URL-based filtering */}
              <Route path="/compliance/:issueType/:clientId/:entityId" element={<ComplianceDetailPage />} />

              {/* AML Detail Page */}
              <Route path="/aml/:issueType/:clientId/:entityId" element={<AMLDetailPage />} />

              {/* Backward compatibility - redirect old compliance URLs */}
              <Route
                path="/compliance/:clientId/:entityId"
                element={<ComplianceRedirectWithEntity />}
              />
              <Route
                path="/compliance/:clientId"
                element={<ComplianceRedirectWithoutEntity />}
              />
              
              {/* User Management */}
              <Route 
                path="/settings/users" 
                element={
                  <ProtectedRoute requiredRole="firm_admin">
                    <UserManagementPage />
                  </ProtectedRoute>
                } 
              />
              
              {/* Entity Management */}
              <Route path="/clients/:clientId/entities" element={<EntityManagement />} />
              <Route path="/clients/:clientId/entities/:entityId" element={<EntityManagement />} />
              <Route path="/clients/:clientId/entities/:entityId/exemptions" element={<EntityExemptionsPage />} />
              <Route path="/clients/:clientId/entities/:entityId/transaction-exemptions" element={<TransactionExemptionsPage />} />
              <Route path="/custom-scan/:clientId/:entityId" element={<CustomScanPage />} />
              <Route path="/custom-scan/:clientId" element={<CustomScanPage />} />
              <Route path="/email-forwarding/:clientId/:entityId" element={<EmailForwardingPage />} />
              <Route
                path="/analytics/email-forwarding/:clientId/:entityId"
                element={
                  <ProtectedRoute requiredRole="firm_admin">
                    <EmailForwardingAnalyticsPage />
                  </ProtectedRoute>
                }
              />

              {/* Compliance Settings - Entity-level threshold configuration (firm_admin only) */}
              <Route
                path="/entities/:entityId/compliance-settings"
                element={
                  <ProtectedRoute requiredRole="firm_admin">
                    <ComplianceSettingsPage />
                  </ProtectedRoute>
                }
              />

              {/* AI Training Pipeline */}
              <Route path="/ai-documents/:clientId/:entityId" element={<AIDocumentsPage />} />
              <Route path="/ai-documents/:clientId/:entityId/review/:documentId" element={<AIDocumentReviewPage />} />
              <Route path="/ai-dashboard/:clientId/:entityId" element={<AIConfidenceDashboardPage />} />
            </Route>

            {/* Legacy redirect for old prepayments route */}
            <Route
              path="/prepayments"
              element={<Navigate to="/accpay/prepayments" replace />}
            />

            {/* Protected routes without sidebar */}
            <Route
              path="/clients/:clientId/xero/select-organization"
              element={
                <ProtectedRoute>
                  <XeroOrganizationSelector />
                </ProtectedRoute>
              }
            />

            <Route
              path="/clients/:clientId/entities/:entityId/setup"
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <EntitySetupWizard />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Xero Configure Redirect - handles OAuth callback redirects */}
            <Route
              path="/clients/:clientId/xero/configure"
              element={
                <ProtectedRoute>
                  <XeroConfigureRedirect />
                </ProtectedRoute>
              }
            />

            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>

        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
